import {
  MasterBarcodeEntry,
  MasterCropData,
  MasterBarcodeStoreDetail,
  MasterDesignData,
  MasterUPCData,
} from '../types';
import { normalizeBarcode } from './normalizeBarcode';

export interface DesignBarcodeInfo {
  barcode?: string;
  normalizedBarcode?: string;
  priority: number;
  productLabel?: string;
}

export interface DerivedBarcodeRow {
  key: string;
  upc: string;
  normalizedBarcode: string;
  entry: MasterBarcodeEntry;
  matchedDesigns: MasterDesignData[];
  designCount: number;
  cropCount: number;
}

export type BarcodeRowAction =
  | { kind: 'move-to-design'; designId: string; barcode: string }
  | { kind: 'create-design'; barcode: string }
  | { kind: 'blocked'; barcode: string }
  | { kind: 'missing' };

function dedupeStoreDetails(details: MasterBarcodeStoreDetail[]): MasterBarcodeStoreDetail[] {
  const seen = new Set<string>();

  return details.filter((detail) => {
    const signature = JSON.stringify({
      storeLabel: detail.storeLabel,
      role: detail.role,
      name: detail.name || '',
      casePack: detail.casePack ?? null,
      images: detail.images.map((image) => ({ url: image.url, sourceLabel: image.sourceLabel || '' })),
    });

    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function mergeBarcodeEntries(entries: MasterBarcodeEntry[]): MasterBarcodeEntry[] {
  const merged = new Map<string, MasterBarcodeEntry>();
  const orderedKeys: string[] = [];

  entries.forEach((entry, index) => {
    const normalized = normalizeBarcode(entry.upc);
    const mergeKey = normalized || `raw:${entry.upc}:${index}`;
    const existing = merged.get(mergeKey);

    if (!existing) {
      merged.set(mergeKey, {
        upc: entry.upc,
        provenance: [...entry.provenance],
        storeDetails: dedupeStoreDetails([...entry.storeDetails]),
      });
      orderedKeys.push(mergeKey);
      return;
    }

    const provenance = [...existing.provenance];
    entry.provenance.forEach((label) => {
      if (!provenance.includes(label)) provenance.push(label);
    });

    merged.set(mergeKey, {
      upc: existing.upc,
      provenance,
      storeDetails: dedupeStoreDetails([...existing.storeDetails, ...entry.storeDetails]),
    });
  });

  return orderedKeys
    .map((key) => merged.get(key))
    .filter((entry): entry is MasterBarcodeEntry => Boolean(entry));
}

export function getAllBarcodeEntries(upc: MasterUPCData): MasterBarcodeEntry[] {
  return [
    ...(upc.barcodeSummary?.primaryUpcs ?? []),
    ...(upc.barcodeSummary?.alternateUpcs ?? []),
    ...(upc.barcodeSummary?.casePacks ?? []),
  ];
}

export function buildBarcodeEntryLookup(entries: MasterBarcodeEntry[]): Map<string, MasterBarcodeEntry> {
  return new Map(
    entries
      .map((entry) => [normalizeBarcode(entry.upc), entry] as const)
      .filter(([normalized]) => Boolean(normalized))
      .filter(([normalized], index, all) => all.findIndex(([value]) => value === normalized) === index)
  );
}

export function getFirstScanBarcode(crops: MasterCropData[]): string | undefined {
  return [...crops]
    .filter((crop) => crop.source === 'scan' && normalizeBarcode(crop.barcode))
    .sort((left, right) => left.id.localeCompare(right.id))[0]?.barcode;
}

export function resolveDraftDesignVerifiedBarcode(
  crops: MasterCropData[],
  _fallbackBarcode?: string | null
): string | undefined {
  const barcodeCounts = new Map<string, { count: number; firstIndex: number }>();

  crops.forEach((crop, index) => {
    if (crop.source !== 'scan') return;
    const barcode = typeof crop.barcode === 'string' ? crop.barcode.trim() : '';
    if (!barcode) return;

    const existing = barcodeCounts.get(barcode);
    if (existing) {
      existing.count += 1;
      return;
    }
    barcodeCounts.set(barcode, { count: 1, firstIndex: index });
  });

  const winningEntry = [...barcodeCounts.entries()].sort((left, right) => {
    if (left[1].count !== right[1].count) return right[1].count - left[1].count;
    if (left[1].firstIndex !== right[1].firstIndex) return left[1].firstIndex - right[1].firstIndex;
    return left[0].localeCompare(right[0]);
  })[0];

  return winningEntry?.[0];
}

export function getDesignBarcodeInfo(
  design: MasterDesignData,
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>
): DesignBarcodeInfo {
  const representativeCrop = design.crops.find((crop) => crop.id === design.representativeCropId) || design.crops[0];
  const firstScanBarcode = getFirstScanBarcode(design.crops);
  const barcodeCandidates = [
    { value: design.barcode, priority: 1 },
    { value: representativeCrop?.source === 'scan' ? representativeCrop.barcode : undefined, priority: 2 },
    { value: firstScanBarcode, priority: 3 },
  ];
  const selected = barcodeCandidates.find((candidate) => normalizeBarcode(candidate.value));
  const normalizedBarcode = normalizeBarcode(selected?.value);
  const matchedEntry = normalizedBarcode ? barcodeEntryByNormalized.get(normalizedBarcode) : undefined;
  const displayDetail = matchedEntry?.storeDetails[0];

  return {
    barcode: matchedEntry?.upc ?? selected?.value,
    normalizedBarcode,
    priority: selected?.priority ?? 99,
    productLabel: displayDetail?.name
      ? `${displayDetail.name}${displayDetail.casePack ? ` (${displayDetail.casePack})` : ''}`
      : undefined,
  };
}

export function resolveDesignTargetBarcode(design: MasterDesignData, upc: MasterUPCData): string | undefined {
  const lookup = buildBarcodeEntryLookup(getAllBarcodeEntries(upc));
  return getDesignBarcodeInfo(design, lookup).barcode;
}

export function compareDesignsByBarcode(
  left: MasterDesignData,
  right: MasterDesignData,
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>
): number {
  const leftInfo = getDesignBarcodeInfo(left, barcodeEntryByNormalized);
  const rightInfo = getDesignBarcodeInfo(right, barcodeEntryByNormalized);
  const leftHasBarcode = leftInfo.normalizedBarcode ? 0 : 1;
  const rightHasBarcode = rightInfo.normalizedBarcode ? 0 : 1;

  if (leftHasBarcode !== rightHasBarcode) return leftHasBarcode - rightHasBarcode;
  if (leftInfo.normalizedBarcode !== rightInfo.normalizedBarcode) {
    return (leftInfo.normalizedBarcode || '').localeCompare(rightInfo.normalizedBarcode || '');
  }
  if (leftInfo.priority !== rightInfo.priority) return leftInfo.priority - rightInfo.priority;

  return left.id.localeCompare(right.id);
}

export function getSortedDesigns(
  upc: MasterUPCData,
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>
): MasterDesignData[] {
  return [...upc.designs].sort((left, right) => compareDesignsByBarcode(left, right, barcodeEntryByNormalized));
}

export function buildDerivedBarcodeRows(upc: MasterUPCData): {
  barcodeRows: DerivedBarcodeRow[];
  caseRows: DerivedBarcodeRow[];
  unmatchedDesigns: MasterDesignData[];
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>;
} {
  const mergedBarcodeEntries = mergeBarcodeEntries([
    ...(upc.barcodeSummary?.primaryUpcs ?? []),
    ...(upc.barcodeSummary?.alternateUpcs ?? []),
  ]);
  const caseEntries = upc.barcodeSummary?.casePacks ?? [];
  const barcodeEntryByNormalized = buildBarcodeEntryLookup([
    ...mergedBarcodeEntries,
    ...caseEntries,
  ]);
  const sortedDesigns = getSortedDesigns(upc, barcodeEntryByNormalized);
  const matchedNormalizedBarcodes = new Set<string>();

  const barcodeRows = mergedBarcodeEntries.map((entry, index) => {
    const normalizedBarcode = normalizeBarcode(entry.upc) || `row-${index}`;
    const matchedDesigns = sortedDesigns.filter((design) => (
      getDesignBarcodeInfo(design, barcodeEntryByNormalized).normalizedBarcode === normalizedBarcode
    ));

    matchedNormalizedBarcodes.add(normalizedBarcode);

    return {
      key: `barcode-${normalizedBarcode}-${index}`,
      upc: entry.upc,
      normalizedBarcode,
      entry,
      matchedDesigns,
      designCount: matchedDesigns.length,
      cropCount: matchedDesigns.reduce((sum, design) => sum + design.crops.length, 0),
    };
  });

  const caseRows = caseEntries.map((entry, index) => {
    const normalizedBarcode = normalizeBarcode(entry.upc) || `case-${index}`;
    return {
      key: `case-${normalizedBarcode}-${index}`,
      upc: entry.upc,
      normalizedBarcode,
      entry,
      matchedDesigns: [],
      designCount: 0,
      cropCount: 0,
    };
  });

  const unmatchedDesigns = sortedDesigns.filter((design) => {
    const info = getDesignBarcodeInfo(design, barcodeEntryByNormalized);
    return !info.normalizedBarcode || !matchedNormalizedBarcodes.has(info.normalizedBarcode);
  });

  return {
    barcodeRows,
    caseRows,
    unmatchedDesigns,
    barcodeEntryByNormalized,
  };
}

export function resolveBarcodeRowAction(upc: MasterUPCData, barcode: string): BarcodeRowAction {
  const row = buildDerivedBarcodeRows(upc).barcodeRows.find((entry) => entry.upc === barcode);

  if (!row) return { kind: 'missing' };
  if (row.designCount > 1) return { kind: 'blocked', barcode: row.upc };
  if (row.designCount === 0) return { kind: 'create-design', barcode: row.upc };

  return {
    kind: 'move-to-design',
    barcode: row.upc,
    designId: row.matchedDesigns[0].id,
  };
}
